import {
  BarChart3,
  FileText,
  Calendar,
  Users,
  TrendingUp,
  AlertCircle,
  Clock,
  CheckCircle
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { caseService } from '../../services/caseService'
import { deadlineService } from '../../services/deadlineService'

const Dashboard = () => {
  // Fetch real statistics from backend
  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      try {
        const [caseStats, deadlineStats] = await Promise.all([
          caseService.getStats(),
          deadlineService.getStats(),
        ])
        // Map to the expected shape
        return {
          totalCases: caseStats.total || 0,
          activeCases: caseStats.open || 0,
          totalDocuments: 0, // TODO: implement document stats endpoint
          pendingDeadlines: deadlineStats.pending || 0,
          teamMembers: 0, // TODO: implement user stats
          completedThisWeek: 0, // TODO: implement weekly completion stats
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error)
        // Return fallback values
        return {
          totalCases: 0,
          activeCases: 0,
          totalDocuments: 0,
          pendingDeadlines: 0,
          teamMembers: 0,
          completedThisWeek: 0,
        }
      }
    },
  })

  const recentActivities = [
    { id: 1, user: 'John Doe', action: 'uploaded document', target: 'Contract v2.pdf', time: '10 min ago' },
    { id: 2, user: 'Jane Smith', action: 'commented on', target: 'Case #12345', time: '25 min ago' },
    { id: 3, user: 'Mike Johnson', action: 'created deadline', target: 'Hearing preparation', time: '1 hour ago' },
    { id: 4, user: 'Sarah Wilson', action: 'assigned case', target: 'Case #12346 to John', time: '2 hours ago' },
  ]

  const upcomingDeadlines = [
    { id: 1, case: 'Smith vs. Johnson', type: 'Hearing', dueDate: 'Tomorrow', priority: 'high' },
    { id: 2, case: 'Corporate Merger', type: 'Document filing', dueDate: 'In 3 days', priority: 'medium' },
    { id: 3, case: 'Patent Application', type: 'Response to USPTO', dueDate: 'In 5 days', priority: 'low' },
  ]

  const statCards = [
    {
      title: 'Total Cases',
      value: stats?.totalCases || 0,
      icon: BarChart3,
      color: 'bg-primary-500',
      change: '+2 this week',
    },
    {
      title: 'Active Cases',
      value: stats?.activeCases || 0,
      icon: AlertCircle,
      color: 'bg-warning-500',
      change: '3 awaiting review',
    },
    {
      title: 'Documents',
      value: stats?.totalDocuments || 0,
      icon: FileText,
      color: 'bg-success-500',
      change: '+15 this month',
    },
    {
      title: 'Pending Deadlines',
      value: stats?.pendingDeadlines || 0,
      icon: Calendar,
      color: 'bg-danger-500',
      change: '2 overdue',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome back! Here's what's happening with your cases.</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className="btn-secondary flex items-center">
            <Clock className="mr-2 h-4 w-4" />
            Last 7 days
          </button>
          <button className="btn-primary flex items-center">
            <TrendingUp className="mr-2 h-4 w-4" />
            Generate Report
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => {
          const Icon = card.icon
          return (
            <div key={card.title} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{card.value}</p>
                  <p className="mt-1 text-sm text-gray-500">{card.change}</p>
                </div>
                <div className={`${card.color} rounded-lg p-3`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            <button className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </button>
          </div>
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <Users className="h-4 w-4 text-primary-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{activity.user}</span>{' '}
                    {activity.action}{' '}
                    <span className="font-medium">{activity.target}</span>
                  </p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Upcoming Deadlines</h2>
            <button className="text-sm text-primary-600 hover:text-primary-700">
              View calendar
            </button>
          </div>
          <div className="space-y-4">
            {upcomingDeadlines.map((deadline) => (
              <div key={deadline.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">{deadline.case}</p>
                  <p className="text-sm text-gray-600">{deadline.type}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{deadline.dueDate}</p>
                  <span className={`badge ${
                    deadline.priority === 'high' ? 'badge-danger' :
                    deadline.priority === 'medium' ? 'badge-warning' : 'badge-success'
                  }`}>
                    {deadline.priority}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Overall progress</span>
              <span className="font-medium text-gray-900">85% on track</span>
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
              <div className="h-full w-4/5 rounded-full bg-success-500"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <button className="flex flex-col items-center justify-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <FileText className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">New Document</span>
          </button>
          <button className="flex flex-col items-center justify-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <Calendar className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Add Deadline</span>
          </button>
          <button className="flex flex-col items-center justify-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <Users className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Create Case</span>
          </button>
          <button className="flex flex-col items-center justify-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <CheckCircle className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Generate Report</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Dashboard